import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { API } from '../App';
import { 
  Play, Check, Star, ArrowRight, Sparkles, Shield, 
  FileText, Video, Users, Zap, Globe, MessageCircle,
  ChevronRight, CreditCard, Award, Target, Rocket,
  BookOpen, CheckCircle2, Clock, Download, Lightbulb,
  TrendingUp, MapPin, GraduationCap, HeadphonesIcon,
  PiggyBank, Brain, FileCheck, Briefcase, Mic
} from 'lucide-react';

// TypeWriter Animation Component
function TypeWriter({ texts, typingSpeed = 80, deletingSpeed = 40, pauseDuration = 2500 }) {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const currentText = texts[currentIndex];

  useEffect(() => {
    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(pauseTimer);
    }

    if (isDeleting) {
      if (displayText === '') {
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % texts.length);
        return;
      }
      const deleteTimer = setTimeout(() => {
        setDisplayText((prev) => prev.slice(0, -1));
      }, deletingSpeed);
      return () => clearTimeout(deleteTimer);
    }

    if (displayText === currentText) {
      setIsPaused(true);
      return;
    }

    const typeTimer = setTimeout(() => {
      setDisplayText((prev) => currentText.slice(0, prev.length + 1));
    }, typingSpeed);
    return () => clearTimeout(typeTimer);
  }, [displayText, isDeleting, isPaused, currentText, texts.length, typingSpeed, deletingSpeed, pauseDuration]);

  return React.createElement('span', { className: 'inline-block' },
    displayText,
    React.createElement('span', { className: 'typing-cursor text-red-500 font-light' }, '|')
  );
}

// Hero Video Component - Bunny Stream
function HeroVideo() {
  var videoUrl = "https://iframe.mediadelivery.net/play/683427/c0d41aad-b50b-46de-a377-711ce149d456?responsive=true";
  
  return React.createElement('div', { className: 'hero-video-container' },
    React.createElement('iframe', {
      src: videoUrl,
      loading: 'lazy',
      className: 'absolute inset-0 w-full h-full border-0',
      allow: 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture',
      allowFullScreen: true,
      title: 'Innovator Founder Visa Introduction'
    })
  );
}

function LandingPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [prices, setPrices] = useState({
    video: { price: 29.99, currency: 'GBP' },
    complete: { price: 149.00, currency: 'GBP' },
    premium: { price: 1999.00, currency: 'GBP' }
  });
  const [visitorCountries, setVisitorCountries] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    // Fetch dynamic prices
    axios.get(API + '/payments/prices')
      .then(res => {
        if (res.data) {
          setPrices(res.data);
        }
      })
      .catch(err => {
        console.log('Using default prices');
      });
    
    // Track visitor and get country info
    trackVisitor();
    
    // Fetch visitor countries for display
    axios.get(API + '/visitors/countries')
      .then(res => {
        if (res.data.countries) {
          setVisitorCountries(res.data.countries.slice(0, 20));
        }
      })
      .catch(err => console.log('Could not fetch visitor countries'));
  }, []);

  const trackVisitor = async () => {
    try {
      // Get visitor's country using CORS-enabled IP geolocation (ipwho.is)
      const geoRes = await fetch('https://ipwho.is/');
      const geoData = await geoRes.json();
      
      await axios.post(API + '/visitors/track', {
        country_code: geoData.country_code || 'XX',
        country_name: geoData.country || 'Unknown',
        page: '/'
      });
    } catch (err) {
      // Track without country if geo fails
      axios.post(API + '/visitors/track', {
        country_code: 'XX',
        country_name: 'Unknown',
        page: '/'
      }).catch(() => {});
    }
  };

  const handleBuyNow = (packageId) => {
    // Redirect to registration page with selected package
    navigate(`/register?package=${packageId}`);
  };

  return (
    <div className="min-h-screen bg-silver-gradient text-slate-900 overflow-x-hidden" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header" data-testid="nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span className="text-slate-900">innovatorfounder</span>
                <span className="text-gradient-red">visas</span>
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="nav-link">Features</a>
              <a href="#pricing" className="nav-link">Pricing</a>
              <Link to="/partnerships" className="nav-link">Partnerships</Link>
              <Link to="/support" className="nav-link">Support</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="btn-ghost" data-testid="nav-login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="nav-register-btn">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Matching Reference Design */}
      <section className="relative pt-28 pb-20 overflow-hidden" data-testid="hero-section">
        {/* Animated Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating UK/Business Icons */}
          <motion.div 
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-32 left-[5%] w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center opacity-60"
          >
            <Globe className="w-8 h-8 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute top-48 right-[8%] w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center opacity-50"
          >
            <FileText className="w-7 h-7 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -15, 0], x: [0, 10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-32 left-[10%] w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center opacity-40"
          >
            <Award className="w-6 h-6 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, 20, 0], rotate: [0, -8, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            className="absolute top-64 left-[15%] w-10 h-10 bg-red-100 rounded-full flex items-center justify-center opacity-50"
          >
            <Rocket className="w-5 h-5 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -18, 0], x: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-40 right-[12%] w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center opacity-40"
          >
            <TrendingUp className="w-7 h-7 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, 12, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            className="absolute top-40 right-[20%] w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center opacity-50"
          >
            <Briefcase className="w-5 h-5 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -10, 0], x: [0, 5, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
            className="absolute bottom-24 left-[25%] w-9 h-9 bg-red-100 rounded-full flex items-center justify-center opacity-30"
          >
            <Target className="w-4 h-4 text-red-400" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            className="absolute top-56 left-[3%] w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center opacity-40"
          >
            <GraduationCap className="w-4 h-4 text-red-400" />
          </motion.div>
        </div>

        {/* Background decorations */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        
        <div className="container-wide relative z-10">
          {/* Hero Title - Centered */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            className="text-center max-w-4xl mx-auto mb-10"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-6">
              Innovator Founder Visa 
              <span className="block text-gradient-red">Masterclass</span>
              <span className="block">& Business Planning Software</span>
            </h1>
            <Link to="/register">
              <Button className="btn-primary text-lg px-8 py-4" data-testid="hero-cta-btn">
                Enroll Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>

          {/* Video with floating features - Wider Layout */}
          <div className="relative flex items-center justify-center gap-6 lg:gap-8">
            {/* Left floating features - 4 items */}
            <div className="hidden lg:flex flex-col gap-3 w-60">
              <FeaturePillNew icon={CheckCircle2} text="Step-by-Step Visa Application Guidance" />
              <FeaturePillNew icon={FileText} text="Technical & Financial Plan System Included" />
              <FeaturePillNew icon={Mic} text="Endorsement Interview Preparation" />
              <FeaturePillNew icon={MapPin} text="Pathway To UK Settlement In 3 Years" />
            </div>

            {/* Center Video/Image - Wider Landscape Rectangle */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl flex-shrink-0 w-full max-w-3xl"
            >
              <div className="aspect-video relative bg-slate-900 rounded-2xl overflow-hidden">
                <HeroVideo />
              </div>
            </motion.div>

            {/* Right floating features - 4 items */}
            <div className="hidden lg:flex flex-col gap-3 w-60">
              <FeaturePillNew icon={Brain} text="AI Idea Validation Assistant" align="right" />
              <FeaturePillNew icon={Sparkles} text="Lifetime Access & Updates" align="right" />
              <FeaturePillNew icon={Video} text="One-to-One Zoom Strategy Sessions" align="right" />
              <FeaturePillNew icon={HeadphonesIcon} text="Dedicated Support Ticket System" align="right" />
            </div>
          </div>

          {/* Mobile features - shown below video on smaller screens */}
          <div className="flex flex-wrap justify-center gap-3 mt-8 lg:hidden">
            <FeaturePillMobile icon={CheckCircle2} text="Step-by-Step Guidance" />
            <FeaturePillMobile icon={Brain} text="AI Idea Validation" />
            <FeaturePillMobile icon={Video} text="Zoom Strategy Sessions" />
            <FeaturePillMobile icon={HeadphonesIcon} text="Dedicated Support" />
          </div>
        </div>
      </section>

      {/* Proposal System Showcase Section - RIGHT AFTER HERO */}
      <section className="section-padding bg-gradient-to-br from-slate-50 via-white to-red-50/30 relative overflow-hidden" data-testid="proposal-section">
        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-100 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-slate-200 rounded-full blur-3xl opacity-50" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-red-50 to-transparent rounded-full blur-3xl opacity-30" />
        </div>

        <div className="container-wide relative z-10">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="badge-red mb-4">AI-Powered Business Planning</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Build Your <span className="text-gradient-red">Endorsement-Ready</span> Business Plan
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Our intelligent proposal system guides you through every section endorsement bodies want to see
            </p>
          </motion.div>

          {/* Main Content - Features + Visual */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Feature List */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Section Categories - First 3 visible, rest blurred */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/25">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">15+ Comprehensive Sections</h3>
                    <p className="text-slate-500 text-sm">Everything endorsement bodies look for</p>
                  </div>
                </div>
                
                {/* Visible Sections - First 3 */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {[
                    'Executive Summary', 'The Problem', 'Our Solution'
                  ].map(function(section, idx) {
                    return React.createElement('div', {
                      key: idx,
                      className: 'flex items-center gap-2 text-sm'
                    },
                      React.createElement(CheckCircle2, { className: 'w-4 h-4 text-green-500 flex-shrink-0' }),
                      React.createElement('span', { className: 'text-slate-700 font-medium' }, section)
                    );
                  })}
                </div>
                
                {/* Blurred Sections - Rest of them */}
                <div className="relative">
                  <div className="grid grid-cols-2 gap-3 blur-[6px] select-none pointer-events-none">
                    {[
                      'Vision & Mission', 'USPs', 'Business Objectives', 'Technology Stack',
                      'Five-Year Plan', 'Innovation Factor', 'Viable Business', 'Scalable Business', 'Market Research'
                    ].map(function(section, idx) {
                      return React.createElement('div', {
                        key: idx,
                        className: 'flex items-center gap-2 text-sm'
                      },
                        React.createElement(CheckCircle2, { className: 'w-4 h-4 text-green-500 flex-shrink-0' }),
                        React.createElement('span', { className: 'text-slate-700' }, section)
                      );
                    })}
                  </div>
                  {/* Overlay with unlock message */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-slate-200">
                      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-500" />
                        +12 more sections - Unlock with signup
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Generation Feature */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">AI Auto-Generation</h3>
                    <p className="text-slate-400 text-sm">Let AI write your sections in seconds</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Simply describe your business idea, and our AI will generate professional, 
                  endorsement-optimized content for each section. Edit and refine to make it yours.
                </p>
              </div>

              {/* Key Benefits */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3">
                    <Download className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">Export to DOCX</h4>
                  <p className="text-slate-500 text-xs">Download professional Word documents</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-3">
                    <Target className="w-5 h-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">Progress Tracking</h4>
                  <p className="text-slate-500 text-xs">Visual completion indicators</p>
                </div>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Browser Window Mockup */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                {/* Browser Header */}
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 ml-4">
                    <div className="bg-white rounded-lg px-4 py-1.5 text-xs text-slate-500 max-w-xs">
                      innovatorfoundervisas.com/dashboard
                    </div>
                  </div>
                </div>
                
                {/* App Interface */}
                <div className="p-6 bg-slate-50">
                  <div className="flex gap-4">
                    {/* Sidebar Preview */}
                    <div className="w-48 bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">Proposal Sections</p>
                      <div className="space-y-2">
                        {['Executive Summary', 'The Problem', 'Our Solution', 'Vision & Mission', 'USPs', 'Objectives'].map(function(item, idx) {
                          return React.createElement('div', {
                            key: idx,
                            className: 'flex items-center gap-2 py-1.5 px-2 rounded-lg ' + (idx === 0 ? 'bg-red-50 border border-red-200' : '') + (idx > 2 ? ' blur-[3px] opacity-60' : '')
                          },
                            React.createElement('div', { 
                              className: 'w-4 h-4 rounded-full flex items-center justify-center ' + (idx < 3 ? 'bg-green-500' : 'bg-slate-200')
                            }, idx < 3 && React.createElement(Check, { className: 'w-2.5 h-2.5 text-white' })),
                            React.createElement('span', { 
                              className: 'text-xs ' + (idx === 0 ? 'font-medium text-red-700' : 'text-slate-600')
                            }, item)
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Content Area Preview */}
                    <div className="flex-1 bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-slate-900">Executive Summary</h4>
                        <div className="flex items-center gap-2">
                          <button className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-lg flex items-center gap-1 shadow-sm">
                            <Sparkles className="w-3 h-3" />
                            AI Generate
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-slate-100 rounded-full w-full" />
                        <div className="h-3 bg-slate-100 rounded-full w-5/6" />
                        <div className="h-3 bg-slate-100 rounded-full w-4/5" />
                        <div className="h-3 bg-slate-100 rounded-full w-full" />
                        <div className="h-3 bg-slate-100 rounded-full w-3/4" />
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                        <button className="px-4 py-2 bg-slate-900 text-white text-xs rounded-lg">
                          Save Section
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-slate-900">94%</p>
                    <p className="text-xs text-slate-500">Success Rate</p>
                  </div>
                </div>
              </div>

              {/* Floating Feature Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 shadow-xl text-white">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <span className="font-semibold text-sm">AI Powered</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/register">
              <Button className="btn-primary text-lg px-8 py-4">
                Start Building Your Plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-slate-500 text-sm mt-4">Included in Complete & Premium packages</p>
          </motion.div>
        </div>
      </section>

      {/* Opportunity For Section - Animated Text */}
      <section className="py-16 bg-gradient-to-br from-slate-50 via-red-50/30 to-slate-50 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-red-100 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-slate-200 rounded-full blur-3xl" />
        </div>
        
        <div className="container-wide relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-5xl mx-auto"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-3">
              It's an excellent opportunity for
            </h2>
            <div className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gradient-red min-h-[1.4em] py-2 whitespace-nowrap overflow-hidden" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic' }}>
              {React.createElement(TypeWriter, {
                texts: [
                  'Entrepreneurs',
                  'Degree Holders in the UK',
                  'Skilled Workers Switching Visa Routes',
                  'PSW Holders',
                  'Global Business Innovators'
                ],
                typingSpeed: 60,
                deletingSpeed: 30,
                pauseDuration: 2500
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Endorsement Mock AI Interview Section - Moved after Hero */}
      <section className="section-padding bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="interview-section">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500 rounded-full blur-3xl" />
        </div>
        
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-6">
                <Mic className="w-4 h-4 mr-2" />
                AI-Powered Interview Prep
              </Badge>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Endorsement <span className="text-gradient-red">Mock Interview</span> System
              </h2>
              
              <p className="text-lg text-slate-300 mb-8">
                Practice your endorsement interview with our advanced AI system before facing the real panel. 
                Get instant feedback, improve your confidence, and master the art of presenting your business case.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Realistic AI Interviewer</h4>
                    <p className="text-slate-400 text-sm">Experience questions just like the real endorsement interview with our intelligent AI</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">10 Comprehensive Questions</h4>
                    <p className="text-slate-400 text-sm">Cover all aspects: business model, innovation, scalability, and UK market strategy</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Instant AI Scoring & Feedback</h4>
                    <p className="text-slate-400 text-sm">Get detailed scores and improvement suggestions after each practice session</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Voice Recognition Technology</h4>
                    <p className="text-slate-400 text-sm">Speak naturally with our speech-to-text system for a realistic interview experience</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => handleBuyNow('complete')}
                  className="btn-primary px-8 py-6 text-lg"
                >
                  Get Started Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>
            
            {/* Right - Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700 shadow-2xl">
                {/* Mock Interview UI Preview */}
                <div className="bg-slate-950 rounded-2xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Mic className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Mock Interview Session</p>
                        <p className="text-red-100 text-sm">Question 3 of 10</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500 text-white">LIVE</Badge>
                  </div>
                  
                  {/* Interview Content */}
                  <div className="p-6">
                    {/* AI Avatar */}
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center border-4 border-red-500/30">
                        <Users className="w-12 h-12 text-slate-400" />
                      </div>
                    </div>
                    
                    {/* Question */}
                    <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
                      <p className="text-slate-300 text-sm mb-2">Current Question:</p>
                      <p className="text-white font-medium">&quot;How does your innovation differ from existing solutions in the UK market?&quot;</p>
                    </div>
                    
                    {/* Recording indicator */}
                    <div className="flex items-center justify-center gap-3 py-4">
                      <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                      <p className="text-slate-400 text-sm">Recording your answer...</p>
                    </div>
                    
                    {/* Controls */}
                    <div className="flex gap-3 justify-center">
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                        Skip Question
                      </Button>
                      <Button size="sm" className="bg-red-500 hover:bg-red-600">
                        Submit Answer
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Floating Stats */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-xl shadow-lg">
                  <p className="text-xs">Average Score</p>
                  <p className="text-xl font-bold">87%</p>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white text-slate-900 px-4 py-2 rounded-xl shadow-lg">
                  <p className="text-xs text-slate-500">Your Goal</p>
                  <p className="text-xl font-bold">Pass with Confidence</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Learn From This Course Section */}
      <section id="features" className="py-20 bg-slate-100 relative overflow-hidden" data-testid="features-section">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left - Title and Feature Cards */}
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <h2 className="text-3xl md:text-4xl font-bold">
                  What You Learn From
                  <span className="block text-gradient-red">This Course</span>
                </h2>
              </motion.div>
              
              {/* 2-column Feature Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <FeatureCardWhite icon={Target} title="Endorsement" description="Aligned with current UK education and policy standards." />
                <FeatureCardWhite icon={Globe} title="English Requirement" description="Structured, compliant routes aligned with UK frameworks." />
                <FeatureCardWhite icon={FileText} title="Technical Plan" description="Step-by-step guide to write winning proposals." />
                <FeatureCardWhite icon={TrendingUp} title="Financial Forecasts" description="Create realistic and compelling financials." />
                <FeatureCardWhite icon={Users} title="Interview Prep" description="Ace your endorsement interview with confidence." />
                <FeatureCardWhite icon={Briefcase} title="Legal Pathways" description="Understand visa rules and requirements clearly." />
              </div>
            </div>

            {/* Right - Large Grayscale Photo */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_fee64ab8-866e-4873-bfd8-a2b3caadae98/artifacts/zi60fx67_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_26_52%20AM.png"
                alt="Ahmad Raza"
                className="w-full max-w-lg ml-auto grayscale object-cover rounded-l-3xl"
                style={{ height: '600px' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three Powerful Plans - Original Layout */}
      <section id="pricing" className="section-padding bg-white" data-testid="pricing-section">
        <div className="container-wide">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="badge-red mb-4">Pricing Plans</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Three <span className="text-gradient-red">Powerful</span> Plans
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your needs. Save thousands compared to traditional immigration consultants.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-start">
            {/* Plan 1 - Video Course */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Video Course</h3>
                <p className="text-sm text-slate-500">Learn the basics</p>
              </div>
              
              <div className="text-center mb-8">
                <span className="text-5xl font-black text-slate-900">£{prices.video.price.toFixed(2)}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                <PricingFeature text="Full video course (7 modules)" />
                <PricingFeature text="Course materials & resources" />
                <PricingFeature text="Community access" />
                <PricingFeature text="Email support" />
                <PricingFeature text="Lifetime updates" />
              </ul>
              
              <Button 
                onClick={() => handleBuyNow('video')}
                className="btn-secondary w-full"
              >
                Buy Now
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>

            {/* Plan 2 - Video Course + Proposal System (Featured) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-xl border-2 border-red-500 relative -mt-4 overflow-visible"
            >
              {/* Badge with white background padding to cover border */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 px-2 bg-white">
                <Badge className="badge-red badge-glow px-4 py-1 bg-red-500 text-white shadow-lg">
                  Recommended
                </Badge>
              </div>
              
              <div className="text-center mb-6 pt-2">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Video Course + Proposal System</h3>
                <p className="text-sm text-slate-500">Complete DIY Package</p>
              </div>
              
              <div className="text-center mb-8">
                <span className="text-5xl font-black text-gradient-red">£{prices.complete.price.toFixed(0)}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                <PricingFeature text="Everything in Video Course" highlight />
                <PricingFeature text="AI Technical Plan Builder" highlight />
                <PricingFeature text="AI Financial Plan Builder" highlight />
                <PricingFeature text="1 Idea Credit included" highlight />
                <PricingFeature text="1x Live Zoom Consultation" highlight />
                <PricingFeature text="Manual Review by Ahmad" highlight />
                <PricingFeature text="Professional Word Templates" highlight />
                <PricingFeature text="Priority support" highlight />
              </ul>
              
              <Button 
                onClick={() => handleBuyNow('complete')}
                className="btn-primary w-full glow-red"
              >
                Buy Now
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>

            {/* Plan 3 - Premium White-Glove Service */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Hire Me To Write Your Plan</h3>
                <p className="text-sm text-slate-500">Premium White-Glove Service</p>
              </div>
              
              <div className="text-center mb-8">
                <span className="text-5xl font-black text-slate-900">£{prices.premium.price.toLocaleString()}</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                <PricingFeature text="Full Technical & Financial Proposal" />
                <PricingFeature text="Market & Competitor Analysis" />
                <PricingFeature text="Two Live Zoom Consultations" />
                <PricingFeature text="Priority WhatsApp Support" />
                <PricingFeature text="Unlimited Revisions" />
              </ul>
              
              <Link to="/white-glove-service" className="block text-center mb-6">
                <span className="text-red-500 font-semibold text-sm hover:underline">View All 17 Services Included →</span>
              </Link>
              
              <Button 
                onClick={() => handleBuyNow('premium')}
                className="btn-secondary w-full"
              >
                Get Started
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding bg-silver-gradient overflow-hidden" data-testid="testimonials-section">
        <div className="container-wide">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="badge-red mb-4">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">
              Over <span className="text-gradient-red">150+</span> Client Says
            </h2>
          </motion.div>
          
          {/* Auto-scrolling testimonial carousel */}
          <div className="relative">
            {/* First row - scrolling left */}
            <div className="flex gap-6 mb-6 testimonial-scroll-left">
              <TestimonialCard 
                name="Sarah Chen"
                role="FinTech Founder"
                image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
                content="The AI system helped me structure my fintech idea perfectly. Got my endorsement on the first try!"
              />
              <TestimonialCard 
                name="Mohammed Al-Rahman"
                role="E-commerce Entrepreneur"
                image="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
                content="Worth every penny. Ahmad's manual review caught issues I would have never noticed. Visa approved!"
              />
              <TestimonialCard 
                name="Priya Sharma"
                role="HealthTech Founder"
                image="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face"
                content="The video course gave me confidence. The Zoom consultation was incredibly valuable."
              />
              <TestimonialCard 
                name="James Wilson"
                role="EdTech Startup CEO"
                image="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
                content="From idea to endorsed business plan in just 3 weeks. The AI tools are incredibly powerful!"
              />
              <TestimonialCard 
                name="Fatima Hassan"
                role="SaaS Founder"
                image="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
                content="The mock interview feature prepared me perfectly. I felt so confident during my actual endorsement interview."
              />
              <TestimonialCard 
                name="David Kim"
                role="PropTech Innovator"
                image="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face"
                content="Best investment I made for my UK visa journey. The templates alone saved me weeks of work."
              />
              <TestimonialCard 
                name="Elena Rodriguez"
                role="GreenTech Entrepreneur"
                image="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face"
                content="Ahmad personally reviewed my application and his feedback was invaluable. Highly recommend the premium package!"
              />
              {/* Duplicate for seamless loop */}
              <TestimonialCard 
                name="Sarah Chen"
                role="FinTech Founder"
                image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
                content="The AI system helped me structure my fintech idea perfectly. Got my endorsement on the first try!"
              />
              <TestimonialCard 
                name="Mohammed Al-Rahman"
                role="E-commerce Entrepreneur"
                image="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
                content="Worth every penny. Ahmad's manual review caught issues I would have never noticed. Visa approved!"
              />
              <TestimonialCard 
                name="Priya Sharma"
                role="HealthTech Founder"
                image="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face"
                content="The video course gave me confidence. The Zoom consultation was incredibly valuable."
              />
              <TestimonialCard 
                name="James Wilson"
                role="EdTech Startup CEO"
                image="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
                content="From idea to endorsed business plan in just 3 weeks. The AI tools are incredibly powerful!"
              />
              <TestimonialCard 
                name="Fatima Hassan"
                role="SaaS Founder"
                image="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
                content="The mock interview feature prepared me perfectly. I felt so confident during my actual endorsement interview."
              />
              <TestimonialCard 
                name="David Kim"
                role="PropTech Innovator"
                image="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face"
                content="Best investment I made for my UK visa journey. The templates alone saved me weeks of work."
              />
              <TestimonialCard 
                name="Elena Rodriguez"
                role="GreenTech Entrepreneur"
                image="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face"
                content="Ahmad personally reviewed my application and his feedback was invaluable. Highly recommend the premium package!"
              />
            </div>
            
            {/* Second row - scrolling right */}
            <div className="flex gap-6 testimonial-scroll-right">
              <TestimonialCard 
                name="Alex Thompson"
                role="AI Startup Founder"
                image="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face"
                content="The document builder generated a professional business plan that impressed the endorsing body. 5 stars!"
              />
              <TestimonialCard 
                name="Aisha Patel"
                role="FoodTech Innovator"
                image="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=face"
                content="I was skeptical at first, but this platform delivered beyond expectations. My visa was approved in record time!"
              />
              <TestimonialCard 
                name="Michael Chang"
                role="CleanTech CEO"
                image="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face"
                content="The Zoom consultation with Ahmad was a game-changer. He identified gaps in my proposal I never would have seen."
              />
              <TestimonialCard 
                name="Sophie Williams"
                role="MedTech Founder"
                image="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face"
                content="Comprehensive, professional, and worth every penny. The AI tools made complex planning simple."
              />
              <TestimonialCard 
                name="Raj Krishnan"
                role="Logistics Tech Founder"
                image="https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face"
                content="From zero knowledge about UK visas to getting endorsed in 2 months. This platform is incredible!"
              />
              <TestimonialCard 
                name="Lisa Anderson"
                role="RetailTech Entrepreneur"
                image="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop&crop=face"
                content="The step-by-step video course made everything clear. No more confusion about what endorsing bodies want."
              />
              <TestimonialCard 
                name="Omar Farouk"
                role="InsurTech Innovator"
                image="https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=100&h=100&fit=crop&crop=face"
                content="I tried doing it myself for months. This platform helped me get it right in just weeks. Amazing value!"
              />
              {/* Duplicate for seamless loop */}
              <TestimonialCard 
                name="Alex Thompson"
                role="AI Startup Founder"
                image="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face"
                content="The document builder generated a professional business plan that impressed the endorsing body. 5 stars!"
              />
              <TestimonialCard 
                name="Aisha Patel"
                role="FoodTech Innovator"
                image="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=face"
                content="I was skeptical at first, but this platform delivered beyond expectations. My visa was approved in record time!"
              />
              <TestimonialCard 
                name="Michael Chang"
                role="CleanTech CEO"
                image="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face"
                content="The Zoom consultation with Ahmad was a game-changer. He identified gaps in my proposal I never would have seen."
              />
              <TestimonialCard 
                name="Sophie Williams"
                role="MedTech Founder"
                image="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face"
                content="Comprehensive, professional, and worth every penny. The AI tools made complex planning simple."
              />
              <TestimonialCard 
                name="Raj Krishnan"
                role="Logistics Tech Founder"
                image="https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face"
                content="From zero knowledge about UK visas to getting endorsed in 2 months. This platform is incredible!"
              />
              <TestimonialCard 
                name="Lisa Anderson"
                role="RetailTech Entrepreneur"
                image="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop&crop=face"
                content="The step-by-step video course made everything clear. No more confusion about what endorsing bodies want."
              />
              <TestimonialCard 
                name="Omar Farouk"
                role="InsurTech Innovator"
                image="https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=100&h=100&fit=crop&crop=face"
                content="I tried doing it myself for months. This platform helped me get it right in just weeks. Amazing value!"
              />
            </div>
            
            {/* Gradient overlays for smooth edges */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* CTA Section with illustration style */}
      <section className="py-16 bg-white" data-testid="cta-mid-section">
        <div className="container-wide">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="flex justify-center">
              <div className="relative w-64 h-64">
                {/* Abstract illustration using shapes */}
                <div className="absolute top-0 left-0 w-20 h-20 bg-red-500 rounded-lg rotate-12 opacity-80" />
                <div className="absolute top-8 right-8 w-16 h-16 bg-amber-400 rounded-full opacity-80" />
                <div className="absolute bottom-8 left-8 w-24 h-24 bg-emerald-400 rounded-2xl -rotate-6 opacity-80" />
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-blue-400 rounded-lg rotate-45 opacity-80" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-purple-500 rounded-full opacity-80" />
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Take the <span className="text-gradient-red">first step</span> toward your future today.
              </h2>
              <p className="text-slate-600 mb-6">
                Join hundreds of successful entrepreneurs who used our system to achieve their UK dream.
              </p>
              <Link to="/register">
                <Button className="btn-primary">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-dark-gradient text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/20 rounded-full blur-3xl" />
        
        <div className="container-wide relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Start Your
              <span className="text-gradient-red"> Journey Now</span>
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              Your UK Innovator Founder Visa success story starts here. Get everything you need for a fraction of the cost.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button className="btn-primary text-lg px-10 py-6 glow-red" data-testid="cta-start-btn">
                  Get Started - £49.99
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Global Visitors Section */}
      {visitorCountries.length > 0 && (
        <section className="py-12 bg-slate-900" data-testid="visitors-section">
          <div className="container-wide">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Badge className="badge-red mb-4">Global Reach</Badge>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Trusted by Founders <span className="text-gradient-red">Worldwide</span>
              </h2>
              <p className="text-slate-400 mb-8">Visitors from around the globe exploring the UK Innovator Visa</p>
              
              <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                {visitorCountries.map((country, index) => (
                  <motion.div
                    key={country.country_code}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-full border border-slate-700 hover:border-red-500 transition-colors"
                  >
                    <img 
                      src={`https://flagcdn.com/24x18/${country.country_code.toLowerCase()}.png`}
                      alt={country.country_name}
                      className="w-6 h-4 rounded-sm object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-white text-sm font-medium">{country.country_name}</span>
                    <span className="text-slate-500 text-xs">({country.visitors})</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16" data-testid="footer">
        <div className="container-wide">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand Column */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">innovatorfoundervisas.com</span>
              </div>
              <p className="text-slate-400 text-sm">
                AI-powered UK Innovator Founder Visa preparation platform. Helping entrepreneurs from Pakistan, UAE, India, Saudi Arabia, Australia & New Zealand achieve their UK business dreams.
              </p>
            </div>
            
            {/* Services Column */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Our Services</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><span className="hover:text-white transition-colors cursor-pointer">AI Business Plan Builder</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Mock Interview System</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">VC Discovery Engine</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Endorsement Preparation</span></li>
                <li><Link to="/white-glove-service" className="hover:text-white transition-colors">White Glove Service</Link></li>
              </ul>
            </div>
            
            {/* Target Audience Column */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Who We Help</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>Entrepreneurs seeking UK Visa</li>
                <li>PSW Visa holders switching</li>
                <li>Skilled Worker Visa holders</li>
                <li>International Students</li>
                <li>Immigration Consultants</li>
              </ul>
            </div>
            
            {/* Legal Column */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy (GDPR)</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/support" className="hover:text-white transition-colors">Contact & Support</Link></li>
              </ul>
            </div>
          </div>
          
          {/* SEO-Rich Keywords Section */}
          <div className="border-t border-slate-800 pt-8 mb-8">
            <p className="text-slate-500 text-xs text-center leading-relaxed max-w-4xl mx-auto">
              UK Innovator Founder Visa | UK Startup Visa | UK Business Visa | UK Entrepreneur Visa | Endorsement Body UK | UK Immigration Consultant | PSW to Innovator Visa | Skilled Worker Visa UK | Student Visa UK | Migrate to UK from Pakistan | UK Visa UAE | UK Visa India | UK Visa Saudi Arabia | UK Visa Australia | UK Visa New Zealand | Immigration Consultants | Visa Services Worldwide | Endorsement Interview Preparation | Business Plan for UK Visa Application
            </p>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center">
              © {new Date().getFullYear()} innovatorfoundervisas.com. Created by Ahmad Raza - UK Innovator Founder Visa Holder
            </p>
            <div className="flex items-center gap-4 text-slate-500 text-xs">
              <span>🇬🇧 UK</span>
              <span>🇵🇰 Pakistan</span>
              <span>🇦🇪 UAE</span>
              <span>🇸🇦 KSA</span>
              <span>🇮🇳 India</span>
              <span>🇦🇺 Australia</span>
              <span>🇳🇿 New Zealand</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Floating Feature Pill Component
function FeaturePill({ icon: Icon, text, delay = 0, align = 'left' }) {
  return (
    <motion.div 
      initial={{ opacity: 0, x: align === 'left' ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: delay + 0.5, duration: 0.5 }}
      className={'glass-card rounded-full px-4 py-3 flex items-center gap-2 shadow-lg ' + (align === 'right' ? 'flex-row-reverse' : '')}
    >
      <div className="w-8 h-8 rounded-full gradient-red flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-white" />
      </div>
      <span className="text-sm font-medium text-slate-700 whitespace-nowrap">{text}</span>
    </motion.div>
  );
}

// New Feature Pill - Matching Reference Design
function FeaturePillNew({ icon: Icon, text, align = 'left' }) {
  return (
    <div className={'bg-white rounded-full px-4 py-3 flex items-center gap-3 shadow-md border border-slate-100 ' + (align === 'right' ? 'flex-row-reverse' : '')}>
      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-red-500" />
      </div>
      <span className="text-sm font-semibold text-slate-800">{text}</span>
    </div>
  );
}

// Mobile Feature Pill
function FeaturePillMobile({ icon: Icon, text }) {
  return (
    <div className="glass-card rounded-full px-4 py-2 flex items-center gap-2">
      <div className="w-6 h-6 rounded-full gradient-red flex items-center justify-center">
        <Icon className="w-3 h-3 text-white" />
      </div>
      <span className="text-xs font-medium text-slate-700">{text}</span>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="card-white p-5"
    >
      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-red-500" />
      </div>
      <h3 className="font-bold text-slate-900 mb-1">{title}</h3>
      <p className="text-sm text-slate-500">{description}</p>
    </motion.div>
  );
}

// Compact Feature Card Component
function FeatureCardCompact({ icon: Icon, title, description }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-slate-50 rounded-xl p-4 flex items-center gap-4 hover:bg-slate-100 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl gradient-red flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </motion.div>
  );
}

// Pricing Feature Component
function PricingFeature({ text, highlight = false }) {
  return (
    <li className="flex items-start gap-3">
      <Check className={'w-5 h-5 flex-shrink-0 mt-0.5 ' + (highlight ? 'text-red-500' : 'text-slate-400')} />
      <span className={highlight ? 'text-slate-900 font-medium' : 'text-slate-600'}>{text}</span>
    </li>
  );
}

// White Feature Card - For What You Learn Section
function FeatureCardWhite({ icon: Icon, title, description }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-red-500" />
      </div>
      <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-500">{description}</p>
    </motion.div>
  );
}

// Feature List Item Component - For What You Learn Section
function FeatureListItem({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
      <div className="w-12 h-12 rounded-xl gradient-red flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h3 className="font-bold text-slate-900 mb-1">{title}</h3>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
    </div>
  );
}

// Testimonial Card Component
function TestimonialCard({ name, role, image, content, delay = 0 }) {
  return (
    <div className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
      <div className="flex items-center gap-3 mb-4">
        <img src={image} alt={name} className="w-12 h-12 rounded-full object-cover" />
        <div>
          <p className="font-semibold text-slate-900">{name}</p>
          <p className="text-sm text-slate-500">{role}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3">
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
      </div>
      <p className="text-slate-600 leading-relaxed text-sm">&ldquo;{content}&rdquo;</p>
    </div>
  );
}

export default LandingPage;
